import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  AfterViewInit,
  HostListener
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as showdown from 'showdown';
import { Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChatFireService } from 'src/app/gen2ai/chatfire.service';
import { isVisionPaperclipComposerModel } from 'src/app/gen2ai/vision-paperclip-models';
import { ToastService } from 'src/app/services/premium-toast.service';
import { isClariSignedInOnlyCatalogModel } from 'src/app/shared/clari-chat-model-catalog';
import { SideMenusService } from '../../core/side-menus/side-menus.service';
import { Conversation, Message } from '../../models/chat.model';
import { showdownMarkdownClassesExtension } from '../../shared/markdown/showdown-md-classes.extension';
import { aggressivelyFormatMarkdownForCodeBlocks } from '../../shared/markdown/aggressive-code-format';

import mkPacmanClient from 'pacman-client';

// dialogs
import { DeepseekComponent } from '../dialogs/deepseek/deepseek.component';
import { OpusFourComponent } from '../dialogs/opus-four/opus-four.component';
import { SonnetFourComponent } from '../dialogs/sonnet-four/sonnet-four.component';
import { GptFourOneComponent } from '../dialogs/gpt-four-one/gpt-four-one.component';
import { FourOMiniComponent } from '../dialogs/four-o-mini/four-o-mini.component';
import { CohereRPlusComponent } from '../dialogs/cohere-r-plus/cohere-r-plus.component';
import { OThreeComponent } from '../dialogs/o-three/o-three.component';
import { LlamaThreePointThreeComponent } from '../dialogs/llama-three-point-three/llama-three-point-three.component';
import { MistralLargeComponent } from '../dialogs/mistral-large/mistral-large.component';
import { MaverickComponent } from '../dialogs/maverick/maverick.component';
import { JambaComponent } from '../dialogs/jamba/jamba.component';
import { PixtralImageComponent } from '../dialogs/pixtral-image/pixtral-image.component';
import { GeminiProComponent } from '../dialogs/gemini-pro/gemini-pro.component';
import { ScoutComponent } from '../dialogs/scout/scout.component';
import { GptFiveComponent } from '../dialogs/gpt-five/gpt-five.component';
import { DalEComponent } from '../dialogs/dal-e/dal-e.component';
import { GrokImageComponent } from '../dialogs/grok-image/grok-image.component';
import { StabilityComponent } from '../dialogs/stability/stability.component';
import { HaikuComponent } from '../dialogs/haiku/haiku.component';
import { PalmyraComponent } from '../dialogs/palmyra/palmyra.component';
import { NovaLiteComponent } from '../dialogs/nova-lite/nova-lite.component';
import { NovaProComponent } from '../dialogs/nova-pro/nova-pro.component';
import { GrokComponent } from '../dialogs/grok/grok.component';
import { GptFiveMiniComponent } from '../dialogs/gpt-five-mini/gpt-five-mini.component';
import { CoherecommandrComponent } from '../dialogs/coherecommandr/coherecommandr.component';
import { Llama31Component } from '../dialogs/llama31/llama31.component';
import { NovaPremierComponent } from '../dialogs/nova-premier/nova-premier.component';
import { OpusSixComponent } from '../dialogs/opus-six/opus-six.component';

// NEW dialog
import { AiCatalogComponent } from '../dialogs/ai-catalog/ai-catalog.component';
import { ClariCoachDialogComponent } from '../dialogs/clari-coach-dialog/clari-coach-dialog.component';

// Voice dialog component
import { TheVoiceComponent } from '../dialogs/the-voice/the-voice.component';

import { TourStep, TourService } from 'src/app/authentication/tour.service';

import { MainMenuComponent } from 'src/app/core/side-menus/main-menu/main-menu.component';
import { ReasoningService } from 'src/app/services/reasoning.service';
import { ModelSelectionService } from 'src/app/services/model-selection.service';
import { CLARI_CHAT_MODEL_CATEGORIES } from 'src/app/shared/clari-chat-model-catalog';

import { NotesService } from '../../services/notes.service';

import { ModelSwitchOverlayService } from 'src/app/services/model-switch-overlay.service';
import { ClarityCoachService } from 'src/app/services/clarity-coach.service';
import { GlmComponent } from '../dialogs/glm/glm.component';
import { Deepseekv3Component } from '../dialogs/deepseekv3/deepseekv3.component';
import { GoogleDocsService } from '../../shared/google-docs-integration/google-docs.service';

import jsPDF from 'jspdf';
import * as docx from 'docx';
import { saveAs } from 'file-saver';

// Interface for placeholder options with paired text, logo, and name
interface PlaceholderOption {
  text: string;
  logoUrl: string;
  name: string;
}

@Component({
  selector: 'app-clari-chat',
  templateUrl: './clari-chat.component.html',
  styleUrls: ['./clari-chat.component.scss']
})
export class ClariChatComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;
  o3form: FormGroup;

  prompt = '';
  selectedModel$: any;
  inputText = '';
  selectedFile?: File;
  selectedImagePreview: string | null = null;
  theSummary: string | null = null;
  openActionsMessageId: string | null = null;
  selectedAssistantMessageId: string | null = null;
  collapsedQaBlocks = new Set<string>();
  coachFabBusy = false;
  coachFabText = 'Coach';

  @Input() id!: string;

  currentMessages: Message[] = [];
  currentConversation?: Conversation;
  converter: showdown.Converter;
  convertedMarkdown = '';

  isLoading = false;
  userInput = '';
  private userScrolledUp = false;

  // textarea placeholder magic
  isFocused = false;
  onTextareaFocus() {
    this.isFocused = true;
  }
  onTextareaBlur() {
    this.isFocused = false;
  }

  // Video generation UI properties
  uploadImageFile: File | null = null;
  uploadImagePreviewUrl: string | null = null;

  // top bar controls
  generating = false;

/* ========================================================================== */
/*  Scroll rail (see below)      
/* ========================================================================== */
activeMessageIndex: number = 0;
private msgObserver?: IntersectionObserver;
private msgElements: HTMLElement[] = [];
private forceLastTick = false;
private forceLastTickTimer: any = null;

// Tour state tracking
isTourActive = false;
tourStepId: string | null = null;

private setupMessageObserver(): void {
  if (!this.messagesContainer?.nativeElement) return;

  // Clean up old observer if it exists
  this.msgObserver?.disconnect();

  const container = this.messagesContainer.nativeElement;

  // Capture all user messages
  this.msgElements = Array.from(
    container.querySelectorAll('.message.user-message')
  ) as HTMLElement[];

  if (!this.msgElements.length) return;

  this.msgObserver = new IntersectionObserver(
  entries => {

    //scroll rail
    if (this.forceLastTick) return;

    const candidates = entries.filter(e => e.isIntersecting);
    if (!candidates.length) return;

    const best = candidates.reduce((prev, curr) =>
      (curr.intersectionRatio ?? 0) > (prev.intersectionRatio ?? 0)
        ? curr
        : prev
    );

    const index = this.msgElements.indexOf(best.target as HTMLElement);

    if (index !== -1) {
      this.activeMessageIndex = index;
      this.cdr.detectChanges();
    }
  },
  {
    root: container,
    threshold: [0.2, 0.4, 0.6, 0.8],
    rootMargin: '-20% 0px -55% 0px'
  }
);

  this.msgElements.forEach(el => this.msgObserver?.observe(el));

  if (!this.userScrolledUp) {
    this.activeMessageIndex = this.msgElements.length - 1;
    this.cdr.detectChanges();
  }
}

get userMessages(): Message[] {
  return this.currentMessages.filter(m => m.role === 'user');
}

scrollRailRoman(index: number): string {
  let remaining = index + 1;
  if (remaining <= 0) return 'I';
  const romanMap = [
    { value: 1000, numeral: 'M' },
    { value: 500, numeral: 'D' },
    { value: 100, numeral: 'C' },
    { value: 50, numeral: 'L' },
    { value: 10, numeral: 'X' },
    { value: 5, numeral: 'V' },
    { value: 1, numeral: 'I' }
  ];

  let result = '';
  for (const entry of romanMap) {
    while (remaining >= entry.value) {
      result += entry.numeral;
      remaining -= entry.value;
    }
  }
  return result || 'I';
}

get bookmarkedQuestions(): Message[] {
  return this.currentMessages.filter(m => m.role === 'user' && m.pinned);
}

bookmarkLabel(msg: Message): string {
  const content = this.normalizeMessageContent(msg?.content);
  if (!content) return 'Question';
  const singleLine = content.replace(/\s+/g, ' ').trim();
  return singleLine.length > 60 ? `${singleLine.slice(0, 60)}…` : singleLine;
}

scrollToBookmarked(msg: Message): void {
  if (!msg?.id) return;
  this.scrollToPinnedForId(msg.id);
}

private forceHighlightLastTick(): void {
  this.forceLastTick = true;

  if (this.forceLastTickTimer) {
    clearTimeout(this.forceLastTickTimer);
    this.forceLastTickTimer = null;
  }

  setTimeout(() => {
    this.setupMessageObserver();

    this.activeMessageIndex = this.msgElements.length - 1;
    this.cdr.detectChanges();

    this.forceLastTickTimer = setTimeout(() => {
      this.forceLastTick = false;
    }, 800);
  }, 0);
}

/* ========================================================================== */
/*  Scroll rail (see above)      
/* ========================================================================== */


/* ========================================================================== */
/*  Exporters (PDF & Doc) (see below)      
/* ========================================================================== */

private messageTitle(msg: Message): string {
  if (msg.role === 'user') return 'User Message';
  // assistant
  return msg.modelDisplayName ? `Assistant (${msg.modelDisplayName})` : 'Assistant Message';
}

private messageBody(msg: Message): string { //Markdown for exporters
  return this.stripMarkdownForExport(this.normalizeMessageContent(msg?.content));
}

exportMessageToPDF(msg: Message): void {
  this.openActionsMessageId = null;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  let y = margin;
  const title = this.messageTitle(msg);
  const text = this.messageBody(msg);
  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, margin, y);
  y += 18;
  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(12);

  const lines = doc.splitTextToSize(text, width);
  for (const line of lines) {
    if (y > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      y = margin;
    }
    doc.text(line, margin, y);
    y += 16;
  }

  doc.save('clarichat-message.pdf');
}

exportMessageToDOCX(msg: Message): void {
  this.openActionsMessageId = null;
  const title = this.messageTitle(msg);
  const body = this.messageBody(msg);
  const children: docx.Paragraph[] = [
    new docx.Paragraph({
      text: title,
      heading: docx.HeadingLevel.HEADING_2,
      spacing: { after: 240 }
    })
  ];

  body.split('\n').forEach(line => {
    children.push(new docx.Paragraph({ text: line.trim(), spacing: { after: 120 } }));
  });

  const d = new docx.Document({
    sections: [{ properties: {}, children }]
  });

  docx.Packer.toBlob(d).then(blob => saveAs(blob, 'clarichat-message.docx'));
}

  // ===================== Google Docs (LIVE) =====================

  // Prevent double-clicks per message
  docCreatingById: Record<string, boolean> = {};

  /** Plus-menu: Drive API doc creation from composer */
  composerDriveDocBusy = false;

  // Show Google Doc button only when assistant message is "worth exporting"
  private readonly MIN_GDOC_WORDS = 12;

  private countWords(text: string): number {
    const s = String(text || '').trim();
    if (!s) return 0;
    return s.split(/\s+/).filter(Boolean).length;
  }

  private isLikelyErrorMessage(msg: Message): boolean {
    const anyMsg: any = msg as any;

    // explicit error fields
    if (anyMsg?.error) return true;

    // job/status types
    const status = String(anyMsg?.status || '').toLowerCase();
    if (status === 'failed' || status === 'error' || status === 'cancelled') return true;

    // content heuristics
    const raw = String(this.normalizeMessageContent(msg?.content) || '').toLowerCase();
    if (!raw.trim()) return false;

    const patterns = [
      '[error]',
      'runtime.usercodesyntaxerror',
      'traceback (most recent call last)',
      'exception',
      'stack trace',
      'request failed',
      'lambda error',
      'failed to',
      'error:'
    ];

    return patterns.some((p) => raw.includes(p));
  }

  // Used by the template
  shouldShowCreateGoogleDocButton(msg: Message): boolean {
    if (!msg || msg.role !== 'assistant') return false;

    const content = this.normalizeMessageContent(msg?.content);
    const wc = this.countWords(content);

    if (wc < this.MIN_GDOC_WORDS) return false;
    if (this.isLikelyErrorMessage(msg)) return false;

    return true;
  }

  private async resolveGoogleTokens(): Promise<{ accessToken: string | null; refreshToken: string | null }> {
    // Try to pull tokens from your existing GoogleDocsService if it exposes them,
    // otherwise fallback to localStorage keys.
    const svc: any = this.googleDocsService as any;

    let accessToken: string | null = null;
    let refreshToken: string | null = null;

    try {
      if (typeof svc.getAccessToken === 'function') {
        const v = svc.getAccessToken();
        accessToken = typeof v === 'string' ? v : await v;
      } else if (typeof svc.getTokens === 'function') {
        const toks = await svc.getTokens();
        accessToken = toks?.accessToken || toks?.access_token || null;
        refreshToken = toks?.refreshToken || toks?.refresh_token || null;
      } else if (typeof svc.accessToken === 'string') {
        accessToken = svc.accessToken;
      } else if (typeof svc.googleAccessToken === 'string') {
        accessToken = svc.googleAccessToken;
      }
    } catch {}

    try {
      if (!refreshToken) {
        if (typeof svc.getRefreshToken === 'function') {
          const v = svc.getRefreshToken();
          refreshToken = typeof v === 'string' ? v : await v;
        } else if (typeof svc.refreshToken === 'string') {
          refreshToken = svc.refreshToken;
        } else if (typeof svc.googleRefreshToken === 'string') {
          refreshToken = svc.googleRefreshToken;
        }
      }
    } catch {}

    if (!accessToken) accessToken = localStorage.getItem('google_access_token');
    if (!refreshToken) refreshToken = localStorage.getItem('google_refresh_token');

    return {
      accessToken: accessToken?.trim() || null,
      refreshToken: refreshToken?.trim() || null
    };
  }

  async exportMessageToGoogleDocs(msg: Message): Promise<void> {
    const normalizedContent = this.normalizeMessageContent(msg?.content);
    if (!normalizedContent.trim()) return;
    if (msg.role !== 'assistant') return;
    if (!this.shouldShowCreateGoogleDocButton(msg)) return;

    const id = msg.id || '';
    if (id) {
      if (this.docCreatingById[id]) return;
      this.docCreatingById[id] = true;
    }

    // close any menus
    this.openActionsMessageId = null;

    try {
      this.showActionToast(msg, 'Creating Google Doc…', 'added');

      const timestamp = msg.timestamp ? new Date(msg.timestamp as any).toLocaleString() : '';
      const titleBase = this.messageTitle(msg);
      const title = timestamp ? `${titleBase} (${timestamp})` : titleBase;
      const userPrompt = this.getPreviousUserPrompt(msg);

      const created = await this.googleDocsService.createDocFromAssistantResponse(
        title,
        normalizedContent.trim(),
        userPrompt.trim() || undefined,
        undefined
      );

      if (created?.url) {
        window.open(created.url, '_blank');
      }

      this.showActionToast(msg, 'Created Google Doc ✓', 'added');
    } catch (error) {
      console.error('Failed to create Google Doc:', error);
      this.showActionToast(msg, 'Google Doc creation failed', 'warn');
    } finally {
      if (id) delete this.docCreatingById[id];
      this.openActionsMessageId = null;
    }
  }

  onCreateDocClick(msg: Message, event?: Event): void {
    event?.stopPropagation();
    void this.exportMessageToGoogleDocs(msg);
  }

  private getPreviousUserPrompt(msg: Message): string {
    const messages = this.currentMessages || [];
    let index = -1;
    if (msg.id) {
      index = messages.findIndex((entry) => entry.id === msg.id);
    }
    if (index === -1) {
      index = messages.indexOf(msg);
    }
    for (let i = index - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'user') {
        return this.normalizeMessageContent(messages[i]?.content);
      }
    }
    return '';
  }

exportConversationToPDF(): void {
  if (!this.currentMessages?.length) return;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  let y = margin;

  const add = (title: string, text: string) => {
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(title, margin, y);
    y += 18;

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(12);

    const lines = doc.splitTextToSize(text, width);
    for (const line of lines) {
      if (y > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += 16;
    }
    y += 8;
  };

  this.currentMessages.forEach(msg => add(this.messageTitle(msg), this.messageBody(msg)));

  doc.save('clarichat-conversation.pdf');
}

exportConversationToDOCX(): void {
  if (!this.currentMessages?.length) return;
  const children: docx.Paragraph[] = [];

  this.currentMessages.forEach(msg => {
    children.push(
      new docx.Paragraph({
        text: this.messageTitle(msg),
        heading: docx.HeadingLevel.HEADING_2,
        spacing: { after: 240 }
      })
    );

    this.messageBody(msg).split('\n').forEach(line => {
      children.push(new docx.Paragraph({ text: line.trim(), spacing: { after: 120 } }));
    });
  });

  const d = new docx.Document({
    sections: [{ properties: {}, children }]
  });

  docx.Packer.toBlob(d).then(blob => saveAs(blob, 'clarichat-conversation.docx'));
}

//Markdown for exporters
private stripMarkdownForExport(md: string): string {
  let s = (md || '').toString();

  // Remove heading markers at start of lines: "# ", "## ", ... "###### "
  s = s.replace(/^(\s{0,3})#{1,6}\s+/gm, '$1');

  // Remove horizontal rules lines like "---", "***", "___"
  s = s.replace(/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/gm, '');

  // Convert markdown links: [OpenAI](https://openai.com) -> OpenAI
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');

  // Remove bold/italic markers (**bold**, *italic*, ***both***)
  s = s.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1');
  s = s.replace(/\*([^*]+)\*/g, '$1');

  // Remove inline code backticks
  s = s.replace(/`([^`]+)`/g, '$1');

  // Collapse extra blank lines a bit
  s = s.replace(/\n{3,}/g, '\n\n');

  return s.trim();
}

/* ========================================================================== */
/*  Exporters (PDF & Doc) (see above)      
/* ========================================================================== */



  showPinStack = false;
  togglePinStack(ev?: MouseEvent) {
    ev?.stopPropagation();
    this.showPinStack = !this.showPinStack;
  }

  jumpToPinned(id?: string) {
    if (!id) return;
    this.showPinStack = false;
    this.scrollToPinnedForId(id);
  }

  onPinsHover(open: boolean) {
    if (this.canHover()) this.showPinStack = open;
  }

  private canHover(): boolean {
    return window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches ?? false;
  }

  trackMsg = (_: number, m: Message) => m.id ?? (m.timestamp as any);

  /**
   * Label next to the message timestamp / pin meta (ChatGPT 5.4 flagship → "ChatGPT5.4").
   */
  messageTimestampModelLabel(msg: Message | any): string {
    if (!msg || msg.role !== 'assistant') {
      return '';
    }
    if (this.isChatGpt54FamilyMessage(msg)) {
      return 'ChatGPT5.4';
    }
    return String(msg.modelDisplayName || msg.modelId || '').trim();
  }

  private isChatGpt54FamilyMessage(msg: Message | any): boolean {
    const id = String(msg?.modelId || '')
      .trim()
      .toLowerCase();
    if (['chatgpt54', 'chatgpt', 'chatgpt52', 'chatgpt5', 'o3', 'chagpt54'].includes(id)) {
      return true;
    }
    const label = String(msg?.modelDisplayName || '').toLowerCase();
    return label.includes('chatgpt') && label.includes('5.4');
  }

  private messageKey(msg: Message, index: number): string {
    return msg.id ?? `idx-${index}-${msg.timestamp ?? ''}-${msg.role}`;
  }

  isBlockCollapsed(index: number): boolean {
    const msg = this.currentMessages[index];
    if (!msg) return false;
    return this.collapsedQaBlocks.has(this.messageKey(msg, index));
  }

  shouldHideMessage(msg: Message, index: number): boolean {
    return false;
  }

  toggleQaCollapse(msg: Message, index: number, ev?: MouseEvent): void {
    ev?.stopPropagation();
    const key = this.messageKey(msg, index);
    if (this.collapsedQaBlocks.has(key)) {
      this.collapsedQaBlocks.delete(key);
    } else {
      this.collapsedQaBlocks.add(key);
    }
  }

  private pruneCollapsedQaBlocks(): void {
    const validKeys = new Set<string>();
    this.currentMessages.forEach((message, index) => validKeys.add(this.messageKey(message, index)));

    this.collapsedQaBlocks.forEach(key => {
      if (!validKeys.has(key)) {
        this.collapsedQaBlocks.delete(key);
      }
    });
  }

  onMessageShellClick(msg: Message, ev?: MouseEvent): void {
    // Only assistant messages can be "activated" to show the + actions button.
    if (msg.role !== 'assistant' || !msg.id) {
      this.selectedAssistantMessageId = null;
      this.openActionsMessageId = null;
      return;
    }

    this.selectedAssistantMessageId = msg.id;
  }

  toggleActionsMenu(msg: Message, ev?: MouseEvent): void {
    ev?.stopPropagation();

    const id = msg.id || null;
    if (!id) return;

    // Ensure the + button remains visible for the message being acted on.
    this.selectedAssistantMessageId = id;
    this.openActionsMessageId = this.openActionsMessageId === id ? null : id;
  }

  isActionsMenuOpen(msg: Message): boolean {
    return !!msg.id && this.openActionsMessageId === msg.id;
  }

  onActionPin(msg: Message): void {
    this.togglePinMessage(msg);
    this.openActionsMessageId = null;
  }

  onActionCopy(msg: any, i: number): void {
    this.handleImageAction(msg, i);
    this.showActionToast(msg, 'Copied ✓');
    this.openActionsMessageId = null;
  }

  onActionDelete(msg: Message, index: number): void {
    this.openActionsMessageId = null;

    const nextMessages = [...this.currentMessages];
    const [removed] = nextMessages.splice(index, 1);
    if (!removed) return;

    this.currentMessages = nextMessages;
    this.pinnedMessages = this.currentMessages.filter(m => m.pinned);
    this.pruneCollapsedQaBlocks();
    this.setupMessageObserver();

    if (removed.id && this.actionToastById[removed.id]) {
      delete this.actionToastById[removed.id];
    }

    if (this.currentConversation) {
      const updatedConversation: Conversation = {
        ...this.currentConversation,
        messages: nextMessages,
        updatedAt: new Date() as any
      };
      this.currentConversation = updatedConversation;
      this.chatService.updateConversation(updatedConversation).subscribe();
    }
  }

async onActionCopyToClariChat(msg: Message): Promise<void> {
  try {
    await this.saveMessageToNotes(msg);
    this.showActionToast(msg, 'Added to ClariNotes ✓', 'added');
  } catch (err: any) {
    console.error('Add to ClariNotes failed:', err);

    const msgText = String(err?.message || err || '');

    if (msgText.includes('User not initialized')) {
      this.showActionToast(msg, 'Please log in to save notes', 'warn');
    } else {
      this.showActionToast(msg, 'Failed to save note', 'warn');
    }
  } finally {
    this.openActionsMessageId = null;
  }
}

designMode = false; 

  private injectDesignMessages(): void {
    if (!this.designMode) return;

    const now = new Date().toISOString();

    const demoUser: Message = {
      id: 'demo-user-1',
      role: 'user',
      content: 'Hey Clari, this is a test prompt so I can style the chat bubbles.',
      timestamp: now as any,
      pinned: false,
      modelDisplayName: ''
    } as Message;

    const demoAssistant: Message = {
      id: 'demo-assistant-1',
      role: 'assistant',
      content: `
# H1 — Heading Test

This is a paragraph under H1. It should have clean margin above and below.

---

## H2 — Heading Test

Another paragraph under H2 with **bold text**, *italic text*, and ***bold italic***.

### H3 — Heading Test

Here is a link: [OpenAI](https://openai.com)

#### H4 — Heading Test

##### H5 — Heading Test

###### H6 — Heading Test

---

## Lists

### Unordered List
- Bullet item one
- Bullet item two with *italic*
- Bullet item three with **bold**
  - Nested bullet one
  - Nested bullet two

### Ordered List
1. Numbered item one
2. Numbered item two with \`inline code\`
3. Numbered item three
   1. Nested numbered one
   2. Nested numbered two

---

## Blockquote

> This is a blockquote.  
> It should have padding, a border-left, and look distinct.

---

## Inline Code

Here is inline code: \`npm install clarichat\`

---

## Code Block

\`\`\`ts
function greet(name: string) {
  console.log("Hello " + name);
}
greet("ClariChat");
\`\`\`

---

## Table

| Column A | Column B | Column C |
|---------|----------|----------|
| Row 1   | Data     | Data     |
| Row 2   | More     | Data     |
| Row 3   | Final    | Value    |

---

## Image

![Image Alt Text](https://via.placeholder.com/200)

---

## Text Decorations

This is **bold**, this is *italic*, and this is ~~strikethrough~~.

Here is a <mark>highlighted mark tag</mark> (raw HTML support depends on showdown).
`.trim(),

      timestamp: now as any,
      pinned: false,
      modelDisplayName: 'GPT-5.1 Thinking',
      images: ['https://via.placeholder.com/320x180.png?text=AI+Image']
    } as any;

    this.currentMessages = [demoUser, demoAssistant];

    this.pinnedMessages = this.currentMessages.filter(m => m.pinned);
  }

  @ViewChild('mainSidenav') mainSidenav!: MatSidenav;
  @ViewChild('messagesContainer', { static: false })
  private messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild(MainMenuComponent) mainMenu!: MainMenuComponent;

  // Check if model supports reasoning dropdown in model name (chatgpt, grok, glm, gemini only)
  get showModelNameReasoningDropdown(): boolean {
    const modelId = this.selectedModel;
    const supportedModels = [
      'chatgpt5',
      'chatgpt52',
      'grok',
      'grok41',
      'gemini',
      'gemini3flash',
      'glm47',
      'zai'
    ];
    return supportedModels.includes(modelId);
  }

  // Toggle model name reasoning dropdown
  toggleModelNameReasoningDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.showModelNameReasoningDropdown) {
      this.modelNameReasoningDropdownOpen = false;
      return;
    }
    this.modelNameReasoningDropdownOpen = !this.modelNameReasoningDropdownOpen;
  }

  // Select reasoning level from model name dropdown
  selectReasoningLevelFromModelName(level: 'auto' | 'instant' | 'thinking' | 'pro', event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.reasoningService.setReasoningLevel(level);
    this.modelNameReasoningDropdownOpen = false;
  }

  private ensureChatGpt52ReasoningDefault(modelId: string): void {
    if (modelId !== 'chatgpt52') {
      return;
    }
    this.reasoningService.setReasoningLevel('instant', modelId);
  }

  private ensureGeminiReasoningDefault(modelId: string): void {
    if (modelId !== 'gemini' && modelId !== 'gemini3flash') {
      return;
    }
    const currentLevel = this.reasoningService.getReasoningLevel();
    if (currentLevel === 'auto' || currentLevel === 'instant') {
      this.reasoningService.setReasoningLevel('thinking', modelId);
    }
  }


  onModelNameClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.model-name-arrow')) {
      this.openModelSpecDialog();
    }
  }

  showScrollToBottom = false;
  loading = false;
  markdownOutput = '';
  error: string | null = null;
  isFading = false;

  isLoggedIn = false;
  private uidSub?: Subscription;

  /** Desktop composer placeholder wording starts above 1000px. */
  private readonly composerDesktopMinWidthPx = 1001;
  isDesktopViewport = false;

  // Model name reasoning dropdown (separate from tools menu)
  modelNameReasoningDropdownOpen = false;
  showComposerToolsMenu = false;
  

  /* ──────────────────────────────────────────────────────────────
   * Rotating model subtitle (below)
   * ────────────────────────────────────────────────────────────── */

  private openPinForMessageId: string | null = null;

  showMainSubtitle = true;
  selectedModelName: string = 'Writer X5';
   isTempChatActive = false;
  audioActive = false;
  micMuted = false;

  voiceSessionState: 'idle' | 'active' | 'failed' = 'idle';
  voiceSessionMessage = '';
  voiceFailureMessage = '';
  private prevModelBeforeAudio: string | null = null;
  private subs: Subscription[] = [];
  messageHistory: string[] = [];
  historyIndex = -1;
  private prismLoaded = false;
  private prism: any = null;

  // Placeholder options with paired text, logo URLs, and names
  placeholderOptions: PlaceholderOption[] = [
    { text: 'Democratizing AI Through Clarity & Access', logoUrl: 'assets/svg/sosexy.png', name: 'ClariChat' },
    { text: 'The OpenAI Flagship', logoUrl: 'assets/logos/openai.svg', name: 'ChatGPT 5.4' },
    { text: 'Industries Top Coding Benchmarks ', logoUrl: 'assets/logos/anthropic.svg', name: 'Claude Opus 4.7' },
    { text: '1 Million Token Context Window', logoUrl: 'assets/logos/x5-baby.png', name: 'Palmyra X5' },
    { text: 'Open Source Muscle - Levels The Playing Field', logoUrl: 'assets/logos/deepseek.svg', name: 'DeepSeek R1' },
    { text: 'Edit Tiny Image Details With Object Masking', logoUrl: 'assets/logos/google.svg', name: 'Gemini Pro 3' },
    { text: 'Unleash Innovative Enterprise Software ', logoUrl: 'assets/logos/cohere.svg', name: '⌘ R+' },
    { text: 'Connect The World with Meta AI', logoUrl: 'assets/logos/meta.svg', name: 'Meta AI' }
  ];


  currentLogo: string = this.placeholderOptions[0].logoUrl;
  logoFading = false;
  modelNameFading = false;


  get currentPlaceholderOption(): PlaceholderOption | undefined {
    return this.placeholderOptions[this.placeholderIndex];
  }


  get currentKimName(): string {
    return this.currentPlaceholderOption?.name || 'AI Model';
  }

  // The Original Creator of Best Of Both Worlds (Kim Bielous) The Blue Sister of Pink Brother
  //  - alternate between teal and pink for model name color
  //  - slow letter-by-letter color transition with wave effect
  isShimmering = false;
  private shimmerDirection: 'teal-to-pink' | 'pink-to-teal' = 'teal-to-pink';
  private shimmerLetterIndex = -1; // Track which letter is currently changing
  private shimmerLetters: string[] = [];

  get modelKimColor(): string {
    return this.placeholderIndex % 2 === 0 ? '#008080' : '#800042';
  }

  get modelNameClass(): string {
    if (this.isShimmering) {
      return `model-name shimmer-${this.shimmerDirection}`;
    }
    return 'model-name';
  }
  
  // Split model name into letters for individual animation
  get modelNameLetters(): Array<{ char: string; index: number }> {
    const name = this.currentKimName;
    return Array.from(name).map((char, index) => ({ char, index }));
  }

  // Get color for each letter during shimmer
  getLetterColor(letterIndex: number): string {
    if (!this.isShimmering) return this.modelKimColor;

    const isTealToPink = this.shimmerDirection === 'teal-to-pink';
    const startColor = isTealToPink ? '#008080' : '#800042';
    const endColor = isTealToPink ? '#800042' : '#008080';

    if (letterIndex < this.shimmerLetterIndex) return endColor;
    return startColor;
  }

  // Check if letter should have wave effect (currently transitioning)
  isLetterWaving(letterIndex: number): boolean {
    return this.isShimmering && letterIndex === this.shimmerLetterIndex;
  }

  // ✅ FIX: stable per-letter identity
  trackByLetter = (_: number, item: { char: string; index: number }) => item.index;

  // Get current placeholder text
  get currentPlaceholderText(): string {
    return this.currentPlaceholderOption?.text || '';
  }

  modelCategories = CLARI_CHAT_MODEL_CATEGORIES;

  currentPlaceholder = '';
  private placeholderIndex = 0;
  private charIndex = 0;
  private isDeleting = false;
  private typingTimeout?: any;
  private placeholderRotateTimer: any = null;
  private isRotating = false; // Prevent conflicts during rotation

  private pacmanClient = mkPacmanClient({
    rockmanId: '9718dee6f1404543ba0c8ad27c5be405',
    serverUrl: 'https://de-pacman.sam-media.com/api/v2/mstore',
    impressionNumber: 1,
    signalInterval: 1000
  });

  private readonly routeModelAliases: Record<string, string> = {
    opus6: 'opus46',
    opus7: 'opus47',
    palmyra: 'palmx',
    scoutinstruct: 'scout',
    glm46v: 'zai',
    commandrinstruct: 'commandRInstruct',
    commandlight: 'commandLight',
    novalite: 'novalite',
    llama31instruct: 'llama31Instruct'
  };

  private liveUserIndex: number | null = null;
  private liveAssistantIndex: number | null = null;
  private assistantBuffer = '';
  private aiCatalogRequested = false;
  private autoCloseTopNavTimer: ReturnType<typeof setTimeout> | null = null;

  copiedMessageId: number | null = null;
  pinnedMessages: Message[] = [];
  currentPinnedIndex = -1;
  isSafariBrowser = false;

  constructor(
    private chatService: ChatFireService,
    private sideMenusService: SideMenusService,
    private fb: FormBuilder,  
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private tourService: TourService,
    private notesService: NotesService,
    private modelSwitchOverlay: ModelSwitchOverlayService,
    private route: ActivatedRoute,
    private reasoningService: ReasoningService,
    private googleDocsService: GoogleDocsService,
    private modelSelectionService: ModelSelectionService,
    private coachService: ClarityCoachService,
    private toast: ToastService
  ) {
    this.form = this.fb.group({
      inputText: [''],
      selectedModel: ['palmx'],
      useWebSearch: [{ value: true, disabled: true }]
    });

    this.o3form = this.fb.group({
      effort: ['low']
    });

    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true,
      ghCodeBlocks: true,
      smoothLivePreview: true,
      extensions: [
        showdownMarkdownClassesExtension({
          imageClass: 'md-img',
          textClass: 'md-text',
          aiImageClass: 'ai-image',
          aiImageBucketHint: 'media.geniousvision.com'
        })
      ]
    });
  }

      currentOption: PlaceholderOption = this.placeholderOptions[0];

  /* ──────────────────────────────────────────────────────────────
   * Legacy video methods (removed)
   * ────────────────────────────────────────────────────────────── */

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // ─── PIN STACK: close if click is outside ───
    const insidePins = !!target.closest('.pin-stack-anchor');
    if (!insidePins && this.showPinStack) {
      this.ngZone.run(() => (this.showPinStack = false));
    }

    // ─── MODEL NAME REASONING DROPDOWN: close if click is outside ───
    const insideModelNameSelector = !!target.closest('.clarichat-reasoning-fab');
    const insideReasoningMenu = !!target.closest('.model-name-reasoning-menu-wrapper');
    if (!insideModelNameSelector && !insideReasoningMenu && this.modelNameReasoningDropdownOpen) {
      this.ngZone.run(() => (this.modelNameReasoningDropdownOpen = false));
    }

    // ─── ASSISTANT ACTIONS ───
    const clickedInsideActions = !!target.closest('.assistant-actions');
    if (!clickedInsideActions) {
      this.openActionsMessageId = null;
    }

    // Hide the assistant "+" gutter action unless an assistant message was clicked.
    const clickedInsideAssistantMessage = !!target.closest('.message.assistant-message');
    if (!clickedInsideAssistantMessage) {
      this.selectedAssistantMessageId = null;
    }

    const clickedInsideComposerTools = !!target.closest('.composer-tools');
    if (!clickedInsideComposerTools && this.showComposerToolsMenu) {
      this.ngZone.run(() => (this.showComposerToolsMenu = false));
    }

  }

  openGoogleVideoGenerator() {
    console.log('Google Video Generator');
  }

  private normalizeCoachText(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim();
  }

  private getLatestCoachContext(): { userText: string; assistantText: string; statusCode: number; ok: boolean } | null {
    const recent = [...this.currentMessages].reverse();
    const userMessage = recent.find(message => message.role === 'user' && this.normalizeCoachText(message.content));
    const assistantMessage = recent.find(message => message.role === 'assistant' && this.normalizeCoachText(message.content));

    if (!userMessage || !assistantMessage) {
      return null;
    }

    const userText = this.normalizeCoachText(userMessage.content);
    const assistantText = this.normalizeCoachText(assistantMessage.content);
    if (!userText || !assistantText) {
      return null;
    }

    const statusCodeValue = Number(
      (assistantMessage as any).statusCode ??
      (assistantMessage as any).httpStatus ??
      (assistantMessage as any).responseStatus ??
      200
    );

    return {
      userText,
      assistantText,
      statusCode: Number.isFinite(statusCodeValue) ? statusCodeValue : 200,
      ok: true
    };
  }

  async triggerCoachFab(): Promise<void> {
    const context = this.getLatestCoachContext();
    if (!context) {
      this.toast.show('No successful chat exchange is available for AI Coach.', 'warning');
      return;
    }

    if (context.statusCode !== 200) {
      this.toast.show('AI Coach is only available after a successful response.', 'warning');
      return;
    }

    this.coachFabBusy = true;
    this.coachFabText = 'Working';

    try {
      const response = await this.coachService.requestDoubleCoachDeepSeek({
        ...context,
        preference: this.coachService.getPreference()
      });

      if (response?.status === 'ready') {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('open-ai-coach-menu'));
        }
        this.dialog.open(ClariCoachDialogComponent, {
          width: 'min(760px, 92vw)',
          maxWidth: '92vw',
          maxHeight: '80vh',
          panelClass: 'ai-coach-dialog-panel',
          data: {
            entries: this.coachService.getRecentCoachEntries(10)
          }
        });
        this.coachFabText = 'Ready';
        this.toast.show('AI Coach is ready to review this exchange.', 'success');
        return;
      }

      this.coachFabText = 'Coach';
      this.toast.show(response?.errorMessage ?? 'AI Coach did not produce a valid review.', 'warning');
    } catch (error: any) {
      this.coachFabText = 'Coach';
      this.toast.show(error?.message ?? 'AI Coach is unavailable right now.', 'error');
    } finally {
      this.coachFabBusy = false;
    }
  }

  openGrokVideoGenerator() {
    console.log('Grok Video Generator');
  }

  private async loadPrismIfNeeded(): Promise<void> {
    if (this.prismLoaded) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const core = await import('prismjs');
    await Promise.all([
      import('prismjs/components/prism-markup'),
      import('prismjs/components/prism-markdown'),
      import('prismjs/components/prism-javascript'),
      import('prismjs/components/prism-typescript'),
      import('prismjs/components/prism-json'),
      import('prismjs/components/prism-css'),
      import('prismjs/components/prism-scss'),
      import('prismjs/components/prism-bash'),
      import('prismjs/components/prism-shell-session'),
      import('prismjs/components/prism-python'),
      import('prismjs/components/prism-java'),
      import('prismjs/components/prism-csharp'),
      import('prismjs/components/prism-cpp'),
      import('prismjs/components/prism-yaml'),
      import('prismjs/components/prism-sql')
    ]).catch(() => {});
    this.prism = (window as any).Prism || (core as any);
    this.prismLoaded = !!this.prism;
  }

  async ngAfterViewInit(): Promise<void> {
    this.refreshDesktopViewport();
    if (this.messagesContainer?.nativeElement) {
      this.messagesContainer.nativeElement.addEventListener('scroll', () => {
        const container = this.messagesContainer.nativeElement;
        const threshold = 50;
        const atBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
        this.userScrolledUp = !atBottom;
        this.showScrollToBottom = !atBottom;
      });
    }
    await this.loadPrismIfNeeded();
    this.highlightAll();
  }
  private detectSafariBrowser(): boolean {
    if (typeof navigator === 'undefined') return false;
    const userAgent = navigator.userAgent || '';
    const isSafari = /Safari/i.test(userAgent) &&
      !/Chrome|Chromium|CriOS|FxiOS|Edg|EdgiOS|OPR|Android/i.test(userAgent);
    return isSafari;
  }


  ngOnInit(): void {
    this.chatService.setChatHistoryNamespace('text').subscribe();
    this.scheduleTopNavAutoClose();

    this.isSafariBrowser = this.detectSafariBrowser();
    this.refreshDesktopViewport();
    this.showMainSubtitle = true;
    this.cdr.detectChanges();
    this.selectedModel$ = this.form.get('selectedModel')?.valueChanges;
    this.selectedModel$?.subscribe((modelId: string) => {
      if (modelId) {
        this.syncSelectedModelState(modelId);
      }
    });

    // Listen for openModelSpecDialog event from top navbar
    window.addEventListener('openModelSpecDialog', () => {
      this.openModelSpecDialog();
    });

    window.addEventListener('openAICatalog', () => {
      this.openAICatalog();
    });

    window.addEventListener('clarichatRepeatPrompt', () => {
      this.recallLastMessage();
    });

    window.addEventListener('clarichatExportPdf', () => {
      this.exportConversationToPDF();
    });

    window.addEventListener('clarichatExportDocx', () => {
      this.exportConversationToDOCX();
    });

    window.addEventListener('clarichatStartTour', () => {
      this.startTour();
    });
    
    this.syncSelectedModelState(this.selectedModel, this.selectedModelName);
    // Subscribe to tour state to show/hide navigation arrows
    this.subs.push(
      this.tourService.currentStep$.subscribe(step => {
        this.isTourActive = step !== null;
        this.tourStepId = step?.elementId ?? null;
        this.cdr.detectChanges();
      })
    );

    // Load temporary chat state from localStorage
    this.isTempChatActive = localStorage.getItem('tempChatActive') === 'true';
    
    // Listen for temporary chat toggle events
    window.addEventListener('tempChatToggled', ((event: CustomEvent) => {
      this.isTempChatActive = event.detail.active;
      this.cdr.detectChanges();
    }) as EventListener);

    // Handle conversation ID from route params or query params
    this.route.queryParams.subscribe(params => {
      const selectedModelFromRoute = params['selectedModel'];
      const selectedModelNameFromRoute = params['selectedModelName'];

      if (selectedModelFromRoute) {
        this.applyIncomingModelSelection(selectedModelFromRoute, selectedModelNameFromRoute);
      }

      // If newChat is true, create a fresh conversation
      if (params['newChat'] === 'true') {
        this.chatService.createNewConversation().subscribe(newConv => {
          this.chatService.selectConversation(newConv.id);
        });
        return;
      }

      const conversationId = params['conversationId'] || this.id;
      if (conversationId) {
        this.loadConversationById(conversationId);
      }

      if (params['openAICatalog'] && !this.aiCatalogRequested) {
        this.aiCatalogRequested = true;
        setTimeout(() => this.openAICatalog(), 0);
      }
    });

    if (this.id) {
      this.chatService.selectConversation(this.id);
    }

    this.subs.push(
      this.chatService.currentConversation$.subscribe(async conversation => {
        if (this.designMode) {
          this.injectDesignMessages();
          await this.loadPrismIfNeeded();
          setTimeout(() => this.highlightAll(), 0);
          return;
        }

        if (conversation) {
          this.currentConversation = conversation;
          this.currentMessages = this.filterOutMediaConversationMessages(conversation.messages || []);
          this.pruneCollapsedQaBlocks();
          this.scrollToBottomIfNeeded();
          await this.loadPrismIfNeeded();
          setTimeout(() => this.highlightAll(), 0);
          //scroll rail (below)
          setTimeout(() => {
          this.setupMessageObserver();
          //scroll rail (above)
}, 0);
        }
      }),

      this.chatService.isLoading$.subscribe(async loading => {
        this.isLoading = loading;
        if (!loading) {
          await this.loadPrismIfNeeded();
          setTimeout(() => this.highlightAll(), 0);
        }
      })
    );

    // Replace authService.currentUser$ subscription with chatService.uid$
    this.uidSub = this.chatService.uid$.subscribe(uid => {
      // Determine if user is authenticated (uid not null and not starting with 'anon-')
      const isAuthenticated = !!uid && !uid.startsWith('anon-');
      this.isLoggedIn = isAuthenticated;

      const ctl = this.form.get('useWebSearch');
      if (!ctl) return;

      if (isAuthenticated) {
        ctl.enable({ emitEvent: false });
      } else {
        ctl.setValue(false, { emitEvent: false });
        ctl.disable({ emitEvent: false });
      }

      try {
        this.cdr.markForCheck();
      } catch {}
    });

    this.showMainSubtitle = true;
  this.cdr.detectChanges();

  
  // ──────────────────────────────────────────────
  // Placeholder + rotation (below)
  // ──────────────────────────────────────────────
  setTimeout(() => {
    this.showMainSubtitle = false;
    this.cdr.detectChanges();
    //(no fixed interval)
    this.startPerfectPlaceholderCycle();
  });
  }

  private filterOutMediaConversationMessages(messages: Message[]): Message[] {
    const out: Message[] = [];
    for (let i = 0; i < messages.length; i++) {
      const msg: any = messages[i];
      if (this.isMediaMessage(msg)) {
        continue;
      }

      if (msg?.role === 'user') {
        const next: any = i + 1 < messages.length ? messages[i + 1] : null;
        if (this.isMediaMessage(next)) {
          continue;
        }
      }

      out.push(msg);
    }
    return out;
  }

  private isMediaMessage(msg: any): boolean {
    const modelId = String(msg?.modelId || '').toLowerCase().trim();
    return (
      Boolean(msg?.image || msg?.video || msg?.thumbnail || msg?.videoId) ||
      ['stability', 'stability35l', 'geminiproimage', 'dale', 'dale2legacy', 'veo', 'veo31', 'cogvideox3'].includes(modelId)
    );
  }

  toggleComposerToolsMenu(event?: Event): void {
    event?.stopPropagation();
    this.showComposerToolsMenu = !this.showComposerToolsMenu;
  }

  closeComposerToolsMenu(): void {
    this.showComposerToolsMenu = false;
  }

  openComposerModelInfo(): void {
    this.openModelSpecDialog();
    this.closeComposerToolsMenu();
  }

  openComposerAICatalog(): void {
    this.openAICatalog();
    this.closeComposerToolsMenu();
  }

  openComposerAdvisory(): void {
    this.sideMenusService.openAdvisory();
    this.closeComposerToolsMenu();
  }

  repeatPromptFromComposer(): void {
    this.recallLastMessage();
    this.closeComposerToolsMenu();
  }

  triggerAttachFileFromComposer(): void {
    this.fileInputRef?.nativeElement?.click();
    this.closeComposerToolsMenu();
  }

  exportComposerPdf(): void {
    this.exportConversationToPDF();
    this.closeComposerToolsMenu();
  }

  exportComposerDocx(): void {
    this.exportConversationToDOCX();
    this.closeComposerToolsMenu();
  }

  private getLastAssistantMessage(): Message | null {
    const msgs = this.currentMessages || [];
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i]?.role === 'assistant') {
        return msgs[i];
      }
    }
    return null;
  }

  async createGoogleDocFromComposerQuestion(): Promise<void> {
    const text = this.normalizeMessageContent(this.userInput).trim();
    if (!text) {
      this.toast.show('Type your question in the box first.', 'warning');
      return;
    }
    if (this.composerDriveDocBusy) {
      return;
    }
    this.composerDriveDocBusy = true;
    this.closeComposerToolsMenu();
    try {
      this.toast.show('Sign in with Google if prompted, then your doc will be created…', 'info', 6000);
      const ts = new Date().toLocaleString();
      const title = `ClariChat — Question (${ts})`;
      const created = await this.googleDocsService.createDriveDocWithPlainText(title, text);
      if (created?.url) {
        window.open(created.url, '_blank');
      }
      this.toast.show('Google Doc created.', 'success');
    } catch (err: any) {
      console.error('[ClariChat] Drive doc from question failed:', err);
      this.toast.show(err?.message || 'Could not create Google Doc.', 'error');
    } finally {
      this.composerDriveDocBusy = false;
      this.cdr.markForCheck();
    }
  }

  async createGoogleDocFromLastAssistantResponse(): Promise<void> {
    const msg = this.getLastAssistantMessage();
    if (!msg) {
      this.toast.show('No assistant reply in this chat yet.', 'warning');
      return;
    }
    if (this.isLikelyErrorMessage(msg)) {
      this.toast.show('The last message looks like an error, not a reply to export.', 'warning');
      return;
    }
    const normalized = this.normalizeMessageContent(msg?.content).trim();
    if (!normalized) {
      this.toast.show('The last reply has no text to put in a doc.', 'warning');
      return;
    }
    if (this.composerDriveDocBusy) {
      return;
    }
    this.composerDriveDocBusy = true;
    this.closeComposerToolsMenu();
    try {
      this.toast.show('Sign in with Google if prompted, then your doc will be created…', 'info', 6000);
      const plain = this.stripMarkdownForExport(normalized);
      const ts = msg.timestamp ? new Date(msg.timestamp as any).toLocaleString() : new Date().toLocaleString();
      const titleBase = this.messageTitle(msg);
      const title = `${titleBase} (${ts})`;
      const userPrompt = this.getPreviousUserPrompt(msg).trim();
      const body =
        (userPrompt ? `Question:\n${userPrompt}\n\n---\n\nResponse:\n` : 'Response:\n') + plain;

      const created = await this.googleDocsService.createDriveDocWithPlainText(title, body);
      if (created?.url) {
        window.open(created.url, '_blank');
      }
      this.toast.show('Google Doc created.', 'success');
    } catch (err: any) {
      console.error('[ClariChat] Drive doc from last reply failed:', err);
      this.toast.show(err?.message || 'Could not create Google Doc.', 'error');
    } finally {
      this.composerDriveDocBusy = false;
      this.cdr.markForCheck();
    }
  }

  onPinClick(msg: Message, event: MouseEvent): void {
    event.stopPropagation();
    this.togglePinMessage(msg);
    if (msg.pinned) {
      const idx = this.pinnedMessages.findIndex(m => m.id === msg.id);
      this.currentPinnedIndex = idx >= 0 ? idx : this.currentPinnedIndex;
    } else {
      if (this.pinnedMessages.length === 0) this.currentPinnedIndex = -1;
      else this.currentPinnedIndex = Math.min(this.currentPinnedIndex, this.pinnedMessages.length - 1);
    }
  }

  ngOnDestroy(): void {
    if (this.autoCloseTopNavTimer) {
      clearTimeout(this.autoCloseTopNavTimer);
      this.autoCloseTopNavTimer = null;
    }
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    this.subs.forEach(s => s.unsubscribe());
    this.chatService.stopRealtimeAudio().subscribe({ error: () => {} });
      this.clearVoiceUiState();
    if (this.placeholderRotateTimer) {
      clearInterval(this.placeholderRotateTimer);
      this.placeholderRotateTimer = null;
    }

    if (this.placeholderRotateTimer) {
      clearInterval(this.placeholderRotateTimer);
      this.placeholderRotateTimer = null;
    }

   
    this.uidSub?.unsubscribe();
    this.stopPerfectPlaceholderCycle();
    this.subs.forEach(s => s.unsubscribe());
    this.uidSub?.unsubscribe();

    this.msgObserver?.disconnect(); //scroll rail
  
  }

  // ===================== TOUR HANDLER =====================
  startTour() {
    const steps: TourStep[] = [
      { elementId: 'brandingClariChat', title: 'ClariChat overview', description: 'This workspace is built for fast, structured prompting. Use it to test a question, compare model behavior, and quickly evaluate which AI setup fits the task.' },
      { elementId: 'previousPromptsBtn', title: 'Prompt history', description: 'Reuse previous prompts to maintain consistency across tasks and reduce time spent re-entering context.' },
      { elementId: 'modelSpecsSection', title: 'Model details', description: 'Review the model profile to understand its strengths, constraints, and best-fit use cases before sending a prompt.' },
      { elementId: 'aiCatalogBtn', title: 'AI model selection', description: 'Choose the model that matches the job. Model choice impacts response style, speed, and reasoning depth.' },
      { elementId: 'aiCoachFab', title: 'AI Coach', description: 'The AI Coach reviews the latest successful exchange and explains which model is best suited to the task, why it fits the work, and what tradeoffs are involved in that choice.' },
      { elementId: 'moreSection', title: 'Additional tools', description: 'Access supporting actions and workflow utilities without leaving the current task.' },
      { elementId: 'effort', title: 'Model effort level', description: 'Adjust the reasoning depth based on task complexity. Higher effort can improve analysis for complex work, while lower effort is faster for straightforward prompts.' },
      { elementId: 'chatInputTextarea', title: 'Prompt input', description: 'Enter the task, brief, or question here. The system uses the message context to generate the response.' },
      { elementId: 'sendMessageButton', title: 'Send prompt', description: 'Submit the current prompt and review the model output in context.' },
      { elementId: 'attachImageButton', title: 'Attach media', description: 'Include an image when the task requires visual context or document interpretation.' },
      { elementId: 'micButton', title: 'Voice input', description: 'Use voice input when speaking is faster or more natural than typing.' },
      { elementId: 'placeholderNavArrows', title: 'Model preview', description: 'Browse available model options and compare their presentation before selecting a final model.' },
      { elementId: 'profileMenuSection', title: 'Profile settings', description: 'Manage account preferences and configuration from the profile area.' },
      { elementId: 'loginLink', title: 'Sign in', description: 'Sign in to access personalized features and saved workflow state.' },
      { elementId: 'aiExecsLink', title: 'Multi-model workflows', description: 'Run parallel or comparative requests across multiple AI models to evaluate response quality and fit.' },
      { elementId: 'menuToggleBtn', title: 'Main menu', description: 'Open the main menu for broader controls and workspace tools.', openMenu: true },
      { elementId: 'claritools', title: 'Clarity tools', description: 'Use the available workflow tools to support a more efficient and operationally focused process.' },
    ];
    this.tourService.startTour(steps);
  }

  // ===================== Live transcript helpers =====================
  private upsertLiveUserMessage(text: string): void {
    if (this.liveUserIndex == null) {
      this.currentMessages = [
        ...this.currentMessages,
        {
          role: 'user',
          content: text,
          timestamp: new Date().toISOString() as any
        } as Message
      ];
      this.liveUserIndex = this.currentMessages.length - 1;
    } else {
      this.currentMessages[this.liveUserIndex] = {
        ...this.currentMessages[this.liveUserIndex],
        content: text
      } as Message;
    }
    this.cdr.detectChanges();
    this.scrollToBottomIfNeeded();
  }

  private finalizeLiveUserMessage(): void {
    if (this.liveUserIndex != null) {
      this.liveUserIndex = null;
      this.cdr.detectChanges();
      this.scrollToBottomIfNeeded();
    }
  }
  private clearVoiceUiState(): void {
    this.voiceSessionState = 'idle';
    this.voiceSessionMessage = '';
    this.voiceFailureMessage = '';
    this.cdr.detectChanges();
  }

  private setVoiceSuccessUiState(): void {
    this.voiceSessionState = 'active';
    this.voiceSessionMessage = 'Current voice session has started';
    this.voiceFailureMessage = 'Premium Voice Membership Needed';
    this.cdr.detectChanges();
  }

  private setVoiceFailureUiState(error?: unknown): void {
    let message = 'Premium Voice Membership Needed';

    const raw =
      typeof error === 'string'
        ? error
        : (error as any)?.message ||
          (error as any)?.error?.message ||
          '';

    if (raw && typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed) {
        message = trimmed;
      }
    }

    this.voiceSessionState = 'failed';
    this.voiceSessionMessage = '';
    this.voiceFailureMessage = message;
    this.cdr.detectChanges();
  }

  onPremiumVoiceFeatureClick(): void {
    this.dialog.open(TheVoiceComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }
  private upsertLiveAssistantMessage(text: string): void {
    if (this.liveAssistantIndex == null) {
      this.currentMessages = [
        ...this.currentMessages,
        {
          role: 'assistant',
          content: text,
          timestamp: new Date().toISOString() as any,
          modelDisplayName: 'GPT Realtime (Audio)'
        } as Message
      ];
      this.liveAssistantIndex = this.currentMessages.length - 1;
    } else {
      this.currentMessages[this.liveAssistantIndex] = {
        ...this.currentMessages[this.liveAssistantIndex],
        content: text
      } as Message;
    }
    this.cdr.detectChanges();
    this.scrollToBottomIfNeeded();
  }

  private finalizeLiveAssistantMessage(): void {
    if (this.liveAssistantIndex != null) {
      this.assistantBuffer = '';
      this.liveAssistantIndex = null;
      this.cdr.detectChanges();
      this.scrollToBottomIfNeeded();
    }
  }

  private modelNameFromValue(val: string): string | null {
    if (val === 'gpt_realtime') return 'GPT Realtime (Audio)';
    if (val === 'gpt_realtime_text') return 'GPT Realtime (Text)';
    if (val === 'gpt4o_audio_realtime') return 'GPT Realtime (Audio)';
    if (val === 'gpt4o_audio_text') return 'GPT Realtime (Text)';
    const found = this.modelCategories.flatMap(cat => cat.models).find(m => m.value === val);
    return found?.name || null;
  }

  private normalizeIncomingModelValue(value?: string | null): string {
    const rawValue = String(value || '').trim();
    if (!rawValue) {
      return '';
    }

    return this.routeModelAliases[rawValue.toLowerCase()] || rawValue;
  }

  private syncSelectedModelState(modelId: string, preferredName?: string | null): void {
    const normalizedModelId = this.normalizeIncomingModelValue(modelId);
    if (!normalizedModelId) {
      return;
    }

    const modelName =
      String(preferredName || '').trim() ||
      this.modelNameFromValue(normalizedModelId) ||
      this.selectedModelName ||
      'Select a Model';

    this.selectedModelName = modelName;

    localStorage.setItem('lastSelectedModel', normalizedModelId);
    localStorage.setItem('selectedModelName', modelName);

    this.modelSelectionService.setSelectedModelName(modelName);

    window.dispatchEvent(
      new CustomEvent('selectedModelChanged', {
        detail: { modelId: normalizedModelId, modelName }
      })
    );

    this.ensureChatGpt52ReasoningDefault(normalizedModelId);
    this.ensureGeminiReasoningDefault(normalizedModelId);
  }

  private applyIncomingModelSelection(modelId?: string | null, preferredName?: string | null): void {
    const normalizedModelId = this.normalizeIncomingModelValue(modelId);
    if (!normalizedModelId) {
      return;
    }

    const selectedModelControl = this.form.get('selectedModel');
    if (selectedModelControl && selectedModelControl.value !== normalizedModelId) {
      selectedModelControl.setValue(normalizedModelId, { emitEvent: false });
    }

    this.syncSelectedModelState(normalizedModelId, preferredName);
  }

  openAICatalog(): void {
    const ref = this.dialog.open(AiCatalogComponent, {
      panelClass: 'ai-catalog-dialog-panel',
      width: '900px',
      maxWidth: '98vw',
      height: 'auto',
      maxHeight: '95vh',
      data: {
        categories: this.modelCategories,
        selectedModel: this.selectedModel,
        selectedModelName: this.selectedModelName,
        userEmail: null
      }
    });

ref.afterClosed().subscribe((result?: { name: string; value: string }) => {
  if (!result) return;

  this.applyIncomingModelSelection(result.value, result.name);

  // 🔹 2) SHOW THE "YOU ARE NOW USING XYZ" MOMENT
  this.modelSwitchOverlay.show({
    name: this.selectedModelName,
    value: this.selectedModel,
  });

  this.cdr.markForCheck();
});
  }


private readonly placeholderHoldMs = 2200;
private readonly modelPlaceholderHoldMs = 2200;
private readonly fadeDurationMs = 250;
private readonly shimmerStepMs = 70;
private readonly typingSpeedMs = 120;
private readonly deletingSpeedMs = 100;
private readonly pauseAfterTypingMs = 1500;
private readonly pauseAfterDeletingMs = 500;

private placeholderTimer: any = null;
private shimmerTimer: any = null;

private destroyed = false;
private cycleToken = 0;

private clearPlaceholderTimer(): void {
  if (this.placeholderTimer) {
    clearTimeout(this.placeholderTimer);
    this.placeholderTimer = null;
  }
}

private clearShimmerTimer(): void {
  if (this.shimmerTimer) {
    clearTimeout(this.shimmerTimer);
    this.shimmerTimer = null;
  }
}

private schedulePlaceholder(token: number, delayMs: number, fn: () => void): void {
  this.clearPlaceholderTimer();

  // Run outside Angular so we don't trigger app-wide CD on every tick
  this.ngZone.runOutsideAngular(() => {
    this.placeholderTimer = setTimeout(() => {
      if (this.destroyed || token !== this.cycleToken) return;
      this.ngZone.run(() => fn());
    }, delayMs);
  });
}

private scheduleShimmer(token: number, delayMs: number, fn: () => void): void {
  this.clearShimmerTimer();

  this.ngZone.runOutsideAngular(() => {
    this.shimmerTimer = setTimeout(() => {
      if (this.destroyed || token !== this.cycleToken) return;
      this.ngZone.run(() => fn());
    }, delayMs);
  });
}

private startPerfectPlaceholderCycle(): void {
  this.destroyed = false;
  this.cycleToken++;

  this.placeholderIndex = 0;
  this.applyPlaceholderOption(this.placeholderIndex);
  this.currentPlaceholder = '';

  this.logoFading = false;
  this.modelNameFading = false;

  this.stopShimmer();
  this.startShimmer(this.cycleToken);

  this.cdr.detectChanges();

  this.runCycle(this.cycleToken);
}

private stopPerfectPlaceholderCycle(): void {
  this.destroyed = true;
  this.cycleToken++;
  this.clearPlaceholderTimer();
  this.clearShimmerTimer();
  this.stopShimmer();
}

private transitionToNextOption(token: number, immediate = false): void {
  if (this.destroyed || token !== this.cycleToken) return;

  const prevIndex = this.placeholderIndex;
  const nextIndex = (prevIndex + 1) % this.placeholderOptions.length;

  this.logoFading = true;
  this.modelNameFading = true;
  this.stopShimmer();
  this.cdr.detectChanges();

  const fadeMs = immediate ? 60 : this.fadeDurationMs;

  this.schedulePlaceholder(token, fadeMs, () => {
    this.placeholderIndex = nextIndex;
    this.applyPlaceholderOption(this.placeholderIndex);
    this.currentPlaceholder = '';

    this.logoFading = false;
    this.modelNameFading = false;

    this.shimmerDirection = prevIndex % 2 === 0 ? 'teal-to-pink' : 'pink-to-teal';
    this.startShimmer(token);

    this.cdr.detectChanges();

    this.runCycle(token);
  });
}

fastForwardTransition(): void {
  if (this.destroyed) return;

  const token = ++this.cycleToken;
  this.clearPlaceholderTimer();
  this.clearShimmerTimer();
  this.stopShimmer();

  this.currentPlaceholder = '';
  this.cdr.detectChanges();

  this.transitionToNextOption(token, true);
}

private startShimmer(token: number): void {
  this.stopShimmer();

  this.isShimmering = true;
  this.shimmerLetters = this.currentKimName.split('');
  this.shimmerLetterIndex = -1;

  this.stepShimmer(token);
}

private stepShimmer(token: number): void {
  if (this.destroyed || token !== this.cycleToken || !this.isShimmering) return;

  this.shimmerLetterIndex++;

  // Let index reach length so the last letter completes its transition
  if (this.shimmerLetterIndex >= this.shimmerLetters.length) {
    this.stopShimmer();
    this.cdr.detectChanges();
    return;
  }

  this.cdr.detectChanges();

  this.scheduleShimmer(token, this.shimmerStepMs, () => this.stepShimmer(token));
}

private stopShimmer(): void {
  this.clearShimmerTimer();
  this.isShimmering = false;
  this.shimmerLetterIndex = -1;
}

private runCycle(token: number): void {
  if (this.destroyed || token !== this.cycleToken) return;

  const option = this.placeholderOptions[this.placeholderIndex];
  if (!option) return;

  const fullText = option.text || '';

  this.typeForward(fullText, 0, token, () => {
    this.schedulePlaceholder(token, this.pauseAfterTypingMs, () => {
      this.deleteBackward(fullText, fullText.length, token, () => {
        this.schedulePlaceholder(token, this.pauseAfterDeletingMs, () => {
          this.transitionToNextOption(token);
        });
      });
    });
  });
}

private typeForward(fullText: string, idx: number, token: number, done: () => void): void {
  if (this.destroyed || token !== this.cycleToken) return;

  this.currentPlaceholder = fullText.slice(0, idx);
  this.cdr.detectChanges();

  if (idx >= fullText.length) {
    done();
    return;
  }

  this.schedulePlaceholder(token, this.typingSpeedMs, () => {
    this.typeForward(fullText, idx + 1, token, done);
  });
}

private deleteBackward(fullText: string, idx: number, token: number, done: () => void): void {
  if (this.destroyed || token !== this.cycleToken) return;

  this.currentPlaceholder = fullText.slice(0, idx);
  this.cdr.detectChanges();

  if (idx <= 0) {
    done();
    return;
  }

  this.schedulePlaceholder(token, this.deletingSpeedMs, () => {
    this.deleteBackward(fullText, idx - 1, token, done);
  });
}

// Keep this small + safe: only updates the active option + logo.
// Typing loop owns currentPlaceholder.
private applyPlaceholderOption(index: number): void {
  const option = this.placeholderOptions[index];
  if (!option) return;

  this.currentOption = option;
  this.currentLogo = option.logoUrl;
  this.cdr.detectChanges();
}

  get selectedModel(): string {
    return this.form.get('selectedModel')?.value || 'palmx';
  }

  get showVisionPaperclipComposer(): boolean {
    return isVisionPaperclipComposerModel(this.selectedModel);
  }

  get showPegasusVideoComposerIcon(): boolean {
    return this.selectedModel === 'pegasus';
  }

  onKeyDown(event: KeyboardEvent): void {
  const el = event.target as HTMLTextAreaElement | null;
  if (!el) return;

  // ArrowUp
  if (event.key === 'ArrowUp' && !event.shiftKey) {
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    if (start === 0 && end === 0) {
      event.preventDefault();
      this.recallPreviousMessage();
    }
    return;
  }

  // ArrowDown 
  if (event.key === 'ArrowDown' && !event.shiftKey) {
    const len = el.value?.length ?? 0;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    if (start === len && end === len) {
      event.preventDefault();
      this.recallNextMessage();
    }
    return;
  }
}

  private composingAtTop = false;
  hasSentMessage = false;

composerExpanded = false;
onEnter(event: KeyboardEvent): void {
  event.preventDefault();
  event.stopPropagation();
  this.sendMessage();
  this.composerExpanded = true;
}

onShiftEnter(event: KeyboardEvent, el: HTMLTextAreaElement): void {
  event.preventDefault();
  event.stopPropagation();
  (event as any).stopImmediatePropagation?.();

  // Tell autoresize: do NOT jump to bottom
  this.composingAtTop = true;

  // Preserve current visual scroll position (so it doesn't jump)
  const prevScrollTop = el.scrollTop;

  // Insert newline at caret
  const start = el.selectionStart ?? this.userInput.length;
  const end = el.selectionEnd ?? this.userInput.length;

  this.userInput =
    this.userInput.slice(0, start) + '\n' + this.userInput.slice(end);

  const nextPos = start + 1;

  // Wait for ngModel to update textarea value, then restore caret/scroll
  setTimeout(() => {
    el.focus();
    el.setSelectionRange(nextPos, nextPos);

    // Resize, but keep our scroll pinned
    this.autoResize(el, prevScrollTop);

    // keep "top composing" mode on so repeated Shift+Enter keeps pushing down
    // (we'll turn it off when the user scrolls manually)
  });
}


  // ---------- Copy / images ----------
  async copyMessageContent(message: any, index: number) {
    try {
      let imageSource: string | null = null;

      if (message.image && message.image.length > 100) {
        imageSource = message.image;
      } else if (
        message.images &&
        message.images.length > 0 &&
        message.images[0].length > 100
      ) {
        imageSource = message.images[0];
      }

      if (imageSource) {
        if (
          imageSource.startsWith('data:') ||
          (imageSource.length > 200 && !imageSource.startsWith('http'))
        ) {
          const dataUrl = imageSource.startsWith('data:')
            ? imageSource
            : 'data:image/png;base64,' + imageSource;

          const blob = this.dataURLtoBlob(dataUrl, 'image/png');

          try {
            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
          } catch (err) {
            console.warn('Base64 image copy failed, fallback to base64 text', err);
            await navigator.clipboard.writeText(dataUrl);
          }
        } else if (imageSource.startsWith('http')) {
          try {
            const res = await fetch(imageSource, { mode: 'cors' });
            const blob = await res.blob();
            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
          } catch (err) {
            console.warn('HTTPS image copy failed, fallback to URL text', err);
            await navigator.clipboard.writeText(imageSource);
          }
        } else {
          await navigator.clipboard.writeText(imageSource);
        }
      } else {
        await navigator.clipboard.writeText(this.normalizeMessageContent(message?.content));
      }

      this.copiedMessageId = index;
      setTimeout(() => (this.copiedMessageId = null), 2000);
    } catch (err) {
      console.error('Failed to copy message content:', err);
    }
  }

  private dataURLtoBlob(dataUrl: string, mimeOverride?: string): Blob {
    const [header, base64Data] = dataUrl.split(',');
    if (!base64Data) throw new Error('Invalid data URL');

    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeOverride || (mimeMatch ? mimeMatch[1] : 'image/png');

    const byteString = atob(base64Data);
    const u8arr = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      u8arr[i] = byteString.charCodeAt(i);
    }

    return new Blob([u8arr], { type: mime });
  }

  handleImageAction(msg: any, i: number) {
    if (msg.modelId === 'grokimage') {
      const imageUrl = msg.image || msg.images?.[0];
      if (imageUrl) {
        const newTab = window.open(imageUrl, '_blank');
        if (!newTab) {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.target = '_blank';
          link.download = 'grok-image.png';
          link.click();
        }
      }
    } else {
      this.copyMessageContent(msg, i);
    }
  }

  downloadGrokImage(url: string, filename: string = 'grok-image.png') {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ─────────── Dialog openers ───────────
  openDeepseekDialog() {
    this.dialog.open(DeepseekComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

       openDeepv3Dialog() {
    this.dialog.open(Deepseekv3Component, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }


  openChatGpt5Dialog() {
    this.dialog.open(GptFiveComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }


  openGeminiDialog() {
    this.dialog.open(GeminiProComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openMaverickDialog() {
    this.dialog.open(MaverickComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openScoutDialog() {
    this.dialog.open(ScoutComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openJambaDialog() {
    this.dialog.open(JambaComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openSonnet4Dialog() {
    this.dialog.open(SonnetFourComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openChatGpt41Dialog() {
    this.dialog.open(GptFourOneComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openChatGptMiniDialog() {
    this.dialog.open(FourOMiniComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openCohereRPlusiDialog() {
    this.dialog.open(CohereRPlusComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openLlamaDialog() {
    this.dialog.open(LlamaThreePointThreeComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openMistralLargeDialog() {
    this.dialog.open(MistralLargeComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openPixtralDialog() {
    this.dialog.open(PixtralImageComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openO3Dialog() {
    this.dialog.open(OThreeComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openOpusDialog() {
    this.dialog.open(OpusFourComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'opus-dialog-panel'
    });
  }

  openOpus6Dialog() {
  this.dialog.open(OpusSixComponent, {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'opus6-dialog-panel'
  });
}

  openDalEDialog() {
    this.dialog.open(DalEComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openGrokImageDialog() {
    this.dialog.open(GrokImageComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openStabilityDialog() {
    this.dialog.open(StabilityComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

    openHaikuDialog() {
    this.dialog.open(HaikuComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

     openPalmryaDialog() {
    this.dialog.open(PalmyraComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

       openNovaLiteDialog() {
    this.dialog.open(NovaLiteComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

    openNovaProDialog() {
    this.dialog.open(NovaProComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

      openGLMDialog() {
    this.dialog.open(GlmComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

   openGrokDialog() {
  this.dialog.open(GrokComponent, {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'Sonnet4-dialog-panel'
  });
}

openChatGpt5MiniDialog() {
  this.dialog.open(GptFiveMiniComponent, {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'Sonnet4-dialog-panel'
  });
}

openCohereCommandRDialog(): void {
  this.dialog.open(CoherecommandrComponent, {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'coherecommandr-dialog-panel',
    autoFocus: false
  });
}

openLlama31Dialog() {
  this.dialog.open(Llama31Component, {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'Sonnet4-dialog-panel'
  });
}

openNovaPremierDialog(): void {
  this.dialog.open(NovaPremierComponent, {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'Sonnet4-dialog-panel'
  });
}








  openModelSpecDialog() {
    if (!this.selectedModel) return;
    
    const dialogMap: { [key: string]: () => void } = {
      'opus46': () => this.openOpus6Dialog(),
      'opus6': () => this.openOpus6Dialog(),
      'opus47': () => this.openOpus6Dialog(),
      'opus37': () => this.openOpusDialog(),
      'opus45': () => this.openOpusDialog(),
      'opus': () => this.openOpusDialog(),
      'sonnet45': () => this.openSonnet4Dialog(),
      'chatgpt5': () => this.openChatGpt5Dialog(),
      'chatgpt41': () => this.openChatGpt41Dialog(),
      'gpt41': () => this.openChatGpt41Dialog(),
      'chatgptmini': () => this.openChatGptMiniDialog(),
      'gpt4omini': () => this.openChatGptMiniDialog(),
      'grok': () => this.openGrokDialog(),
      'grok41': () => this.openGrokImageDialog(),
      'grokimage': () => this.openGrokImageDialog(),
      'gemini': () => this.openGeminiDialog(),
      'maverick': () => this.openMaverickDialog(),
      'scout': () => this.openScoutDialog(),
      'jamba': () => this.openJambaDialog(),
      'llama': () => this.openLlamaDialog(),
      'llama3': () => this.openLlamaDialog(),
      'cohere': () => this.openCohereRPlusiDialog(),
      'commandLight': () => this.openCohereRPlusiDialog(),
      'mistrallarge': () => this.openMistralLargeDialog(),
      'mistral': () => this.openMistralLargeDialog(),
      'pixtralimage': () => this.openPixtralDialog(),
      'pixtral': () => this.openPixtralDialog(),
      'o3': () => this.openO3Dialog(),
      'dale': () => this.openDalEDialog(),
      'stability': () => this.openStabilityDialog(),
      'haiku45': () => this.openHaikuDialog(),
      'haiku': () => this.openHaikuDialog(),
      'palmx': () => this.openPalmryaDialog(),
      'palmyra': () => this.openPalmryaDialog(),
      'nova2': () => this.openNovaLiteDialog(),
      'novalite': () => this.openNovaLiteDialog(),
      'novapro': () => this.openNovaProDialog(),
      'zai': () => this.openGLMDialog(),
      'deepseek': () => this.openDeepseekDialog(),
      'deepv3': () => this.openDeepv3Dialog(),
      'commandRInstruct': () => this.openCohereCommandRDialog(),
      'llama31Instruct': () => this.openLlama31Dialog(),
      'premier': () => this.openNovaPremierDialog(),


    };

    const openDialog = dialogMap[this.selectedModel];
    if (openDialog) {
      openDialog();
    }
  }

  // ---------- File/image handling ----------
  onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = e => {
      this.selectedImagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeAttachedImage(): void {
    this.resetUploadState();
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  // ===================== Voice entry point (REAL-TIME TRANSCRIPTION) =====================
  async onAudioIconClick(): Promise<void> {
    try {
      if (this.audioActive) {
        await firstValueFrom(this.chatService.stopRealtimeAudio());
        this.audioActive = false;
        this.micMuted = false;
        this.clearVoiceUiState();
        return;
      }

      this.clearVoiceUiState();

      await firstValueFrom(this.chatService.startRealtimeAudio());
      this.audioActive = true;
      this.micMuted = false;
      this.setVoiceSuccessUiState();
    } catch (error) {
      console.error('Failed to start/stop realtime audio:', error);
      this.audioActive = false;
      this.micMuted = false;
      this.setVoiceFailureUiState(error);
    }
  }

  async handleSendOrAudioClick(): Promise<void> {
    const hasText = !!this.userInput?.trim();
    const hasVisionAttachment =
      this.showVisionPaperclipComposer && !!this.selectedFile;
    if (hasText || hasVisionAttachment) {
      await this.sendMessage();
      return;
    }

    await this.onAudioIconClick();
  }

  toggleMic(): void {
    this.micMuted = !this.micMuted;
    try {
      this.chatService.muteRealtimeMic(this.micMuted);
    } catch {}
  }

  async stopVoice(): Promise<void> {
    try {
      await firstValueFrom(this.chatService.stopRealtimeAudio());
      this.audioActive = false;
      this.micMuted = false;
      this.clearVoiceUiState();
    } catch (error) {
      console.error('Failed to stop realtime audio:', error);
      this.audioActive = false;
      this.micMuted = false;
      this.setVoiceFailureUiState(error);
    }
  }
  // ===================== Core send =====================
  async sendMessage(): Promise<void> {
    const model = this.selectedModel;

    this.resetTextarea();

    if (this.isLoading) return;
    const hasVisionAttachment =
      isVisionPaperclipComposerModel(model) && !!this.selectedFile;
    if (!this.userInput.trim() && !hasVisionAttachment) return;

    if (isClariSignedInOnlyCatalogModel(model)) {
      const authed = await firstValueFrom(this.chatService.isAuthenticated$.pipe(take(1)));
      if (!authed) {
        this.toast.show('Sign in to use this model.', 'info');
        return;
      }
    }

    const prompt = (this.userInput || '').trim();

    if (prompt) {
      this.messageHistory.push(prompt);
      this.historyIndex = -1;
    }

    this.userInput = '';

    const useWeb = this.form.get('useWebSearch')?.value === true;

    const imageFile = isVisionPaperclipComposerModel(model) ? this.selectedFile : undefined;
    this.chatService.sendMessage(prompt, model, useWeb, imageFile).subscribe();
    this.resetUploadState();

    setTimeout(() => this.scrollToBottom(), 50);

    await this.loadPrismIfNeeded();
    setTimeout(() => this.highlightAll(), 0);
  
    this.forceHighlightLastTick(); //scroll tick

    this.hasSentMessage = true;
  }

  @ViewChild('chatTextarea') chatTextarea!: ElementRef<HTMLTextAreaElement>;

autoResize(el: HTMLTextAreaElement, preferredScrollTop?: number): void {
  const MAX = 240;

  if (!el.value || el.value.trim().length === 0) {
    el.style.height = '';
    el.style.overflowY = 'hidden';
    el.scrollTop = 0;
    return;
  }

  const prevScrollTop = preferredScrollTop ?? el.scrollTop;

  const cs = window.getComputedStyle(el);
  const lineH = parseFloat(cs.lineHeight || '20') || 20;

  el.style.height = '';

  const scrollH = el.scrollHeight;
  const clientH = el.clientHeight || lineH;

  const fillRatio = scrollH / clientH;
  const shouldPreGrow = fillRatio >= 0.85;

  const desired = Math.min(scrollH + (shouldPreGrow ? lineH : 0), MAX);

  el.style.height = `${desired}px`;

  const capped = desired >= MAX;
  el.style.overflowY = capped ? 'auto' : 'hidden';

  // KEY FIX:
  // If we are composing at the top, NEVER snap to bottom.
  if (capped) {
    if (this.composingAtTop) {
      el.scrollTop = prevScrollTop; // preserve view near the top
      // If you want it ALWAYS pinned to the top, use this instead:
      // el.scrollTop = 0;
    } else {
      el.scrollTop = el.scrollHeight; // old behavior
    }
  }
}

private resetTextarea(): void {
  const el = this.chatTextarea?.nativeElement;
  if (!el) return;

  el.style.height = '';     
  el.style.overflowY = 'hidden';
  el.scrollTop = 0;
}



  recallPreviousMessage(): void {
    if (this.messageHistory.length === 0) return;
    if (this.historyIndex === -1) {
      this.historyIndex = this.messageHistory.length - 1;
    } else if (this.historyIndex > 0) {
      this.historyIndex--;
    }
    this.userInput = this.messageHistory[this.historyIndex];
  }

  recallNextMessage(): void {
    if (this.messageHistory.length === 0 || this.historyIndex === -1) return;
    if (this.historyIndex < this.messageHistory.length - 1) {
      this.historyIndex++;
      this.userInput = this.messageHistory[this.historyIndex];
    } else {
      this.historyIndex = -1;
      this.userInput = '';
    }
  }

  recallLastMessage(): void {
    if (this.messageHistory.length === 0) return;
    this.userInput = this.messageHistory[this.messageHistory.length - 1];
    this.historyIndex = this.messageHistory.length - 1;
  }

  get latestPromptText(): string {
    return this.messageHistory.length > 0
      ? this.messageHistory[this.messageHistory.length - 1]
      : '';
  }

  private resetUploadState(): void {
    this.selectedFile = undefined;
    this.selectedImagePreview = null;
  }

  convertMarkdown(markdown: unknown): string {
    const normalized = this.normalizeMessageContent(markdown);
    try {
      const aggressive = aggressivelyFormatMarkdownForCodeBlocks(normalized || '');
      return this.converter.makeHtml(aggressive);
    } catch {
      return normalized || '';
    }
  }

  getMessageDisplayContent(msg: Message): string {
    return this.normalizeMessageContent(msg?.content);
  }

  private normalizeMessageContent(content: unknown): string {
    if (typeof content === 'string') {
      const trimmed = content.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          const extracted = this.extractTextFromCohere(parsed);
          if (extracted !== null) return extracted;
        } catch {}
      }
      return content;
    }

    const extracted = this.extractTextFromCohere(content);
    if (extracted !== null) return extracted;

    return content == null ? '' : String(content);
  }

  private extractTextFromCohere(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') return null;
    const record = payload as Record<string, unknown>;
    if (typeof record.text === 'string') return record.text;

    const modelOutput = record.model_output;
    if (typeof modelOutput === 'string') return modelOutput;
    if (modelOutput && typeof modelOutput === 'object') {
      const nested = (modelOutput as Record<string, unknown>).text;
      if (typeof nested === 'string') return nested;
    }

    return null;
  }

  private highlightAll(): void {
    try {
      if (!this.prismLoaded || !this.prism) return;
      const root = this.messagesContainer?.nativeElement || document;
      if (this.prism.highlightAllUnder) {
        this.prism.highlightAllUnder(root);
      } else {
        this.prism.highlightAll();
      }
    } catch {}
  }

  scrollToBottom(): void {
    if (!this.messagesContainer?.nativeElement) return;
    const container = this.messagesContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
    this.userScrolledUp = false;
    this.showScrollToBottom = false;
  }

  private scrollToBottomIfNeeded(): void {
    if (!this.messagesContainer?.nativeElement) return;
    if (!this.userScrolledUp) {
      setTimeout(() => {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }, 100);
    }
  }

  // ===================== Pinning =====================
  togglePinMessage(msg: Message): void {
    msg.pinned = !msg.pinned;
    this.pinnedMessages = this.currentMessages.filter(m => m.pinned);

    if (msg.pinned) {
      this.scrollToPinnedForId(msg.id || '');
    }
  }

  private scrollToPinnedForId(id: string): void {
    const container = this.messagesContainer?.nativeElement as HTMLElement;
    const el = document.getElementById(`msg-${id}`);
    if (!el || !container) return;

    const top = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
    container.scrollTo({ top, behavior: 'smooth' });

    el.classList.add('pinned-highlight');
    setTimeout(() => el.classList.remove('pinned-highlight'), 1000);
  }

  scrollToPinned(next: boolean): void {
    if (this.pinnedMessages.length === 0) return;

    if (next) {
      this.currentPinnedIndex = (this.currentPinnedIndex + 1) % this.pinnedMessages.length;
    } else {
      this.currentPinnedIndex =
        (this.currentPinnedIndex - 1 + this.pinnedMessages.length) %
        this.pinnedMessages.length;
    }

    const targetMessage = this.pinnedMessages[this.currentPinnedIndex];
    const element = document.getElementById(`msg-${targetMessage.id}`);

    if (element) {
      const container = this.messagesContainer?.nativeElement;
      const offsetTop = (element as any).offsetTop - (container as any).offsetTop - 20;
      container.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  }

  toggleUseWebSearch(): void {
    const ctl = this.form.get('useWebSearch');
    if (!ctl || ctl.disabled) {
      return;
    }

    const nextValue = !ctl.value;
    ctl.setValue(nextValue, { emitEvent: true });

    try {
      this.cdr.markForCheck();
    } catch {}
  }

  logout() {
    // Use ChatFireService.logout() to handle logout
    this.chatService.logout().subscribe({
      next: () => {
        // After logout, the uid$ will emit null or new anonymous uid, which will update isLoggedIn automatically.
        this.isLoggedIn = false;
        // Optionally clear local state
        this.form.get('useWebSearch')?.setValue(false);
      },
      error: (err) => {
        console.error('Logout failed', err);
      }
    });
  }

  async saveMessageToNotes(msg: Message): Promise<void> {
    const normalizedContent = this.normalizeMessageContent(msg?.content);
    if (!normalizedContent.trim()) {
      return;
    }

    const who = msg.role === 'user' ? 'You' : msg.modelDisplayName || 'Assistant';

    const timestamp = msg.timestamp ? new Date(msg.timestamp as any).toLocaleString() : '';

    const titleBase = `Chat – ${who}`;
    const title = timestamp ? `${titleBase} (${timestamp})` : titleBase;

    await this.notesService.createNote({
      title,
      content: normalizedContent,
      labels: ['ClariChat'],
      color: '#fff8e1',
      pinned: false
    });
  }

  clearAllPins(ev?: MouseEvent) {
    ev?.stopPropagation();
    this.currentMessages.forEach(m => (m.pinned = false));
    this.pinnedMessages = [];
    this.showPinStack = false;
  }

// ───────── Action toast state ─────────
actionToastById: Record<
  string,
  { text: string; type: 'copied' | 'added' | 'warn' }
> = {};

private showActionToast(
  msg: Message,
  text: string,
  type: 'copied' | 'added' | 'warn' = 'copied'
): void {
  const id = msg.id;
  if (!id) return;

  this.actionToastById[id] = { text, type };

  setTimeout(() => {
    if (this.actionToastById[id]?.text === text) {
      delete this.actionToastById[id];
    }
  }, 3500);
}

  getTextareaPlaceholder(): string {
    // ✅ Special cases: image models get different placeholder
    if (this.selectedModel === 'stability' || this.selectedModel === 'dale' || this.selectedModel === 'grokimage') {
      return `Describe the image you want…`;
    }

    const modelName = this.selectedModelName || 'AI';

    if (this.isDesktopViewport) {
      return `Your Selected AI is ${modelName}`;
    }

    return `Selected AI is ${modelName}`;
  }

  private refreshDesktopViewport(): void {
    if (typeof window === 'undefined') return;
    const next = window.innerWidth >= this.composerDesktopMinWidthPx;
    if (next !== this.isDesktopViewport) {
      this.isDesktopViewport = next;
      this.cdr.markForCheck();
    }
  }

  @HostListener('window:resize')
  onWindowResizeComposerPlaceholder(): void {
    this.refreshDesktopViewport();
  }

  /**
   * Load a conversation by ID (used when clicking from menu)
   */
  private async loadConversationById(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        // Conversation is already set as current in the service
        // The subscription will handle updating currentMessages
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.error('[ClariChat] Error loading conversation', err);
    }
  }

  private scheduleTopNavAutoClose(): void {
    if (this.autoCloseTopNavTimer) {
      clearTimeout(this.autoCloseTopNavTimer);
    }

    this.autoCloseTopNavTimer = setTimeout(() => {
      this.autoCloseTopNavTimer = null;
      if (this.sideMenusService.getMainMenuState()) {
        this.sideMenusService.closeMainMenu();
      }
    }, 45000);
  }

  //scroll rail (below)
  scrollToMessage(index: number): void {
  const el = this.msgElements?.[index];
  if (!el || !this.messagesContainer?.nativeElement) return;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
  //scroll rail (above)



}